import React from 'react';
import { CellProps, Column } from 'react-table';

import { Stream } from 'types/stream';

import { getLumeoFileIdFromStreamUri } from 'services/stream';
import { pluralize } from 'services/string';
import { relativeTimeFromNow } from 'services/date';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteStreams } from 'streams/hooks/useDeleteStreams';
import { useDownloadFile } from 'files/hooks/useDownloadFile';
import { useModal } from 'hooks/useModal';
import { useUpdateStream } from 'streams/hooks/useUpdateStream';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { NameCell, TableLabelsCell } from 'components/Table/Table';
import {
  PaginatedTable,
  PaginatedTableProps,
} from 'components/Table/PaginatedTable';

import { FilesOverviewTableActionsCell } from 'files/views/FilesOverview/Table/ActionsCell';

export type FileTableProps = Pick<
  PaginatedTableProps<Stream>,
  'queryResult' | 'pageSize' | 'page' | 'onPageChange'
>;

export function CameraFileListTable(props: FileTableProps) {
  const { mutate: updateStream } = useUpdateStream();
  const { mutateAsync: deleteStreams, isLoading: isDeleting } =
    useDeleteStreams();
  const { download, isLoading: isDownloading } = useDownloadFile();

  const { open } = useModal();

  const { data: currentPlan, isSuccess } = useCurrentPlan();
  const canCreateTags =
    (isSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);

  const [isRenamingFile, setIsRenamingFile] = React.useState<Stream['id']>();

  const handleRowClick = React.useCallback(
    (file: Stream) => {
      const fileId = getLumeoFileIdFromStreamUri(file);

      if (!fileId) {
        return;
      }

      open('file', { id: fileId });
    },
    [open]
  );

  function handleBulkDownload(fileIds: string[]) {
    fileIds.forEach((file) => download(file));
  }

  const columns = React.useMemo(() => {
    const columns: Column<Stream>[] = [
      {
        id: 'name',
        Header: 'File',
        Cell(props: CellProps<Stream>) {
          function handleRename(name: string) {
            updateStream({ id: props.row.original.id, name });
            setIsRenamingFile(undefined);
          }

          function cancelRename() {
            setIsRenamingFile(undefined);
          }

          return (
            <NameCell
              {...props}
              isRenaming={isRenamingFile === props.row.original.id}
              onNameChange={handleRename}
              onCancel={cancelRename}
              entity="file"
            />
          );
        },
      },
    ];

    if (canCreateTags) {
      columns.push({
        id: 'tags',
        Header: 'Labels',
        Cell(props: CellProps<Stream>) {
          return (
            <TableLabelsCell
              {...props}
              entity="streams"
              listQueryKey={['streams']}
              singleQueryKey={['stream']}
            />
          );
        },
      });
    }

    columns.push(
      {
        id: 'updated_at',
        Header: 'Last Updated',
        sortType: 'date',
        accessor(file: Stream) {
          return relativeTimeFromNow(file.updated_at);
        },
      },
      {
        id: 'actions',
        Header: ' ',
        width: 0,
        Cell(props: CellProps<Stream>) {
          return (
            <FilesOverviewTableActionsCell
              file={props.row.original}
              onViewClick={() => handleRowClick(props.row.original)}
              onRenameClick={setIsRenamingFile}
            />
          );
        },
      }
    );

    return columns;
  }, [canCreateTags, handleRowClick, isRenamingFile, updateStream]);

  return (
    <PaginatedTable
      {...props}
      id="camera-files-table"
      label="camera files"
      columns={columns}
      onRowClick={handleRowClick}
      emptyMessage={
        <EmptyView>This camera has not uploaded clips yet.</EmptyView>
      }
      bulkActions={[
        {
          action: 'Download',
          loading: isDownloading,
          onClick: handleBulkDownload,
        },
        {
          action: 'Delete',
          icon: 'delete',
          intent: 'danger',
          loading: isDeleting,
          onClick: (ids) => {
            open('confirm-delete', {
              title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'stream')}?`,
              onConfirm: () => deleteStreams({ stream_ids: ids }),
            });
          },
        },
      ]}
    />
  );
}
